import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useUrlParam } from '../hooks/useUrlParam';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import { Badge, Button, Checkbox, SearchableSelect, SegmentedControl, StatCard, Table, useToast } from '../components/ui';
import type { TableColumn } from '../components/ui';
import OpportunityPanel from '../components/ideation/OpportunityPanel';
import StatusMenu, { StatusTrigger } from '../components/ideation/StatusMenu';
import { supabase } from '../lib/supabase';
import { uniqueChannelName } from '../lib/realtime';
import { enqueueIdeationJobs } from '../lib/ideationJobs';
import { useUser } from '../context/UserContext';
import { TIERS, type Tier } from '../lib/keywordSweep';
import {
  compareByTier,
  fetchOpportunities,
  moveOpportunities,
  perMonth,
  runDetect,
  seoStageOf,
  seoKind,
  seoStatus,
  SEO_ACTIONS,
  SEO_KINDS,
  SEO_STAGES,
  type SeoActionType,
  type SeoKind,
  type SeoStage,
  type SeoOpportunity,
  type SeoStatus,
} from '../lib/seoOpportunities';

/**
 * Ideation — what Search Console says is wrong, ranked by what fixing it is worth.
 *
 * The list is the product. Every row names one problem, what it would win, and what to do about it;
 * the panel behind a row carries the evidence so a person can disagree with the agent. There is no
 * score anywhere, on purpose — one number invites optimizing the number instead of the site.
 */

type Tab = SeoStage;

const TABS = SEO_STAGES;

const num = (n: number | null | undefined, digits = 0) =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

// "2026-08-19..2026-09-15" → "Aug 19 → Sep 15". Two ISO dates at stat-card size overflowed the card.
const formatWindow = (window: string | null | undefined) => {
  if (!window) return '—';
  const fmt = (iso: string) => {
    const d = new Date(`${iso}T12:00:00`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  return window.split('..').map(fmt).join(' → ');
};

// One-line cell text that clips with an ellipsis instead of spilling into the next column.
// Table cells are nowrap but not overflow-hidden, so long client names ran under the badges.
const clip: CSSProperties = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

export default function Ideation() {
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const { user } = useUser();

  const [rows, setRows] = useState<SeoOpportunity[]>([]);
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // The open finding is ?finding=<id>, so refresh and back keep your place.
  const [openId, setOpenId] = useUrlParam('finding');
  const [search, setSearch] = useState('');

  const tab = (params.get('tab') as Tab) || 'open';
  const clientId = params.get('client');
  const kindFilter = params.get('kind') as SeoKind | null;
  const tierParam = params.get('tier');
  const tierFilter: Tier | null = tierParam === 'A' || tierParam === 'B' ? tierParam : null;

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      setRows(await fetchOpportunities({ clientId }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not load opportunities');
    } finally {
      setLoading(false);
    }
  }, [clientId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    supabase
      .from('clients')
      .select('id, name')
      .eq('service_seo', true)
      .order('name')
      .then(({ data }) => setClients(data ?? []));
  }, []);

  // Diagnosis runs in a background function for minutes. Rows land as they finish rather than
  // all at once at the end, so the screen follows the table instead of polling a promise.
  useEffect(() => {
    const channel = supabase
      .channel(uniqueChannelName('seo_opportunities_channel'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'seo_opportunities' }, () => void load())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [load]);

  const statuses = useMemo<SeoStatus[]>(() => TABS.find((t) => t.id === tab)?.statuses ?? ['open'], [tab]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => statuses.includes(r.status))
      .filter((r) => !kindFilter || r.kind === kindFilter)
      .filter((r) => !tierFilter || r.tier === tierFilter)
      .filter((r) => !q || r.query.toLowerCase().includes(q) || (r.url ?? '').toLowerCase().includes(q))
      // Every A above every B, then clicks: an A page is the one that can turn into a customer.
      .sort(compareByTier);
  }, [rows, statuses, kindFilter, tierFilter, search]);

  const openRows = useMemo(() => rows.filter((r) => r.status === 'open'), [rows]);
  const monthlyUpside = useMemo(
    () =>
      rows
        .filter((r) => r.status === 'open' || r.status === 'diagnosing' || r.status === 'diagnosed')
        .reduce((n, r) => n + (perMonth(r.est_click_gain) ?? 0), 0),
    [rows]
  );

  const kindCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows.filter((x) => statuses.includes(x.status))) counts.set(r.kind, (counts.get(r.kind) ?? 0) + 1);
    return counts;
  }, [rows, statuses]);

  // Counted within the kind filter, so a chip's number is what clicking it shows.
  const tierCounts = useMemo(() => {
    const counts: Record<Tier, number> = { A: 0, B: 0 };
    for (const r of rows) {
      if (!statuses.includes(r.status) || (kindFilter && r.kind !== kindFilter)) continue;
      if (r.tier === 'A' || r.tier === 'B') counts[r.tier] += 1;
    }
    return counts;
  }, [rows, statuses, kindFilter]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onRefresh = async () => {
    setBusy('detect');
    try {
      const result = await runDetect(clientId);
      toast.success(`${result.total ?? 0} opportunities`, clientId ? undefined : 'Across every client with SEO on');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Detection failed');
    } finally {
      setBusy(null);
    }
  };

  // Diagnose queues an agent job — one per client in the selection — rather than calling a model
  // from the browser. The agent claims it off the Spec queue with the repo in hand, and the
  // diagnosis it writes is the spec a person accepts.
  const onDiagnose = async (ids: string[]) => {
    const chosen = rows.filter((r) => ids.includes(r.id));
    if (!chosen.length) return;
    if (!user) {
      toast.error('Sign in again before queueing work');
      return;
    }
    setBusy('diagnose');
    try {
      const jobs = await enqueueIdeationJobs(chosen, { id: user.id, name: user.name });
      toast.success(
        jobs.length === 1 ? 'Queued for the agent' : `Queued ${jobs.length} jobs`,
        `${chosen.length} finding${chosen.length === 1 ? '' : 's'} — it lands in the Spec queue on Fulfillment HQ.`
      );
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not queue the diagnosis');
    } finally {
      setBusy(null);
    }
  };

  // Hand moves, from the Status column or a selection. The row usually leaves this tab, so the
  // toast names where it went.
  const onMove = async (ids: string[], to: SeoStatus) => {
    if (!ids.length) return;
    setBusy('move');
    try {
      await moveOpportunities(ids, to);
      toast.success(`Moved ${ids.length === 1 ? '1 row' : `${ids.length} rows`} to ${seoStageOf(to).label}`, seoStatus(to).label);
      setSelected((prev) => new Set([...prev].filter((id) => !ids.includes(id))));
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not move them');
    } finally {
      setBusy(null);
    }
  };

  const columns: TableColumn<SeoOpportunity>[] = [
    {
      key: 'select',
      label: '',
      width: '3%',
      render: (_v, row) => (
        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
          <Checkbox checked={selected.has(row.id)} onChange={() => toggle(row.id)} />
        </span>
      ),
    },
    {
      key: 'query',
      label: 'Query',
      width: '27%',
      wrap: true,
      render: (_v, row) => (
        <span style={{ display: 'block' }}>
          <span style={{ color: 'var(--text-body)', fontWeight: 'var(--weight-medium)' }}>{row.query}</span>
          {row.url ? (
            <span className="t-num" title={row.url} style={{ ...clip, fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
              {row.url.replace(/^https?:\/\/(www\.)?/, '')}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'tier',
      label: 'Tier',
      width: '5%',
      render: (_v, row) =>
        row.tier ? (
          <Badge tone={TIERS[row.tier].tone} title={row.tier_reason || TIERS[row.tier].blurb}>
            {row.tier}
          </Badge>
        ) : (
          <span style={{ color: 'var(--text-faint)' }}>—</span>
        ),
    },
    ...(clientId
      ? []
      : [
          {
            key: 'client',
            label: 'Client',
            width: '12%',
            render: (_v: unknown, row: SeoOpportunity) => (
              <span title={row.client?.name} style={{ ...clip, color: 'var(--text-muted)' }}>{row.client?.name ?? '—'}</span>
            ),
          } as TableColumn<SeoOpportunity>,
        ]),
    {
      key: 'kind',
      label: 'Diagnosis',
      width: '13%',
      render: (_v, row) => {
        const k = seoKind(row.kind);
        return (
          <Badge tone={k.tone} title={k.blurb}>
            {k.label}
          </Badge>
        );
      },
    },
    {
      key: 'position',
      label: 'Pos',
      align: 'right',
      mono: true,
      width: '7%',
      render: (_v, row) => num(row.baseline?.position, 1),
    },
    {
      key: 'impressions',
      label: 'Impr',
      align: 'right',
      mono: true,
      width: '8%',
      render: (_v, row) => num(row.baseline?.impressions),
    },
    {
      key: 'ctr',
      label: 'CTR',
      align: 'right',
      mono: true,
      width: '7%',
      render: (_v, row) => (row.baseline?.ctr == null ? '—' : `${num(row.baseline.ctr, 1)}%`),
    },
    {
      key: 'gain',
      label: 'Clicks / mo',
      align: 'right',
      mono: true,
      width: '10%',
      render: (_v, row) => {
        const m = perMonth(row.est_click_gain);
        return <span style={{ color: m && m >= 10 ? 'var(--status-up)' : 'var(--text-muted)' }}>{m == null ? '—' : `+${num(m)}`}</span>;
      },
    },
    // On Found every row is unread by definition, so the column was 401 copies of "not read yet".
    ...(tab === 'open'
      ? []
      : [
          {
            key: 'action',
            label: 'Proposed',
            width: '13%',
            render: (_v: unknown, row: SeoOpportunity) =>
              row.action_type ? (
                <span style={{ ...clip, color: 'var(--text-body)' }}>
                  {SEO_ACTIONS[row.action_type as SeoActionType]?.label ?? row.action_type}
                </span>
              ) : row.status === 'diagnosing' ? (
                <span style={{ color: 'var(--status-warn)' }}>with the agent</span>
              ) : (
                <span style={{ color: 'var(--text-faint)' }}>not read yet</span>
              ),
          } as TableColumn<SeoOpportunity>,
        ]),
    ...(tab === 'settled' || tab === 'queued' || tab === 'diagnosed'
      ? [
          {
            key: 'status',
            label: 'Status',
            // Room for the widest badge ("In the queue") plus the Move to chevron.
            width: '12%',
            render: (_v: unknown, row: SeoOpportunity) => (
              <StatusMenu current={row.status} jobOut={row.status === 'diagnosing'} disabled={busy === 'move'} onMove={(to) => onMove([row.id], to)}>
                <StatusTrigger status={row.status} disabled={busy === 'move'} />
              </StatusMenu>
            ),
          } as TableColumn<SeoOpportunity>,
        ]
      : []),
  ];

  const openRow = rows.find((r) => r.id === openId) ?? null;
  // A row already sitting in the queue is not selectable work; queueing it twice would put two
  // jobs on one repo.
  const selectedUndiagnosed = visible.filter((r) => selected.has(r.id) && r.status !== 'diagnosing');
  const selectedRows = visible.filter((r) => selected.has(r.id));
  const selectedStatuses = new Set(selectedRows.map((r) => r.status));

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-canvas)' }}>
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Ideation"
          count={visible.length}
          searchPlaceholder="Search queries and URLs..."
          searchValue={search}
          onSearchChange={setSearch}
          actions={
            <span style={{ display: 'inline-flex', gap: '8px', marginLeft: '6px' }}>
              <Button size="sm" variant="secondary" icon="refresh-cw" onClick={onRefresh} disabled={busy === 'detect'}>
                {busy === 'detect' ? 'Looking...' : 'Find opportunities'}
              </Button>
              {/* Diagnose only exists once there is something to diagnose; a permanently disabled
                  primary button was the loudest thing on the page and did nothing. */}
              {selectedRows.length ? (
                <StatusMenu
                  current={selectedStatuses.size === 1 ? selectedRows[0].status : undefined}
                  jobOut={selectedStatuses.has('diagnosing')}
                  disabled={busy === 'move'}
                  align="right"
                  onMove={(to) => onMove(selectedRows.map((r) => r.id), to)}
                >
                  <Button size="sm" variant="secondary" iconTrailing="chevron-down" disabled={busy === 'move'}>
                    Move {selectedRows.length}
                  </Button>
                </StatusMenu>
              ) : null}
              {selectedUndiagnosed.length ? (
                <Button size="sm" icon="sparkles" onClick={() => onDiagnose(selectedUndiagnosed.map((r) => r.id))} disabled={busy === 'diagnose'}>
                  Diagnose {selectedUndiagnosed.length}
                </Button>
              ) : null}
            </span>
          }
        />

        {/* Three numbers, and each one is a fact rather than a score. */}
        {/* Two-up on phones (Window spans the row, its value is too long for half), auto-fit from md */}
        <div
          className="grid grid-cols-2 md:[grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]"
          style={{ gap: '12px', marginBottom: '18px' }}
        >
          <StatCard label="Waiting to be read" value={String(openRows.length)} />
          <StatCard label="Est. clicks / month" value={`+${num(monthlyUpside)}`} hint="If every open and diagnosed fix landed at its target position" />
          {/* grid so the card stretches to the row height like its siblings */}
          <div className="col-span-2 md:col-span-1 grid">
            <StatCard label="Window" value={formatWindow(rows[0]?.baseline?.window)} hint="Search Console runs two days behind" />
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          {/* On phones the four stages don't fit one row: they scroll sideways rather than
              wrapping each label onto three lines */}
          <div className="max-w-full overflow-x-auto">
            <SegmentedControl
              options={TABS.map((t) => ({
                id: t.id,
                label: (
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {t.label} ({rows.filter((r) => t.statuses.includes(r.status)).length})
                  </span>
                ),
              }))}
              value={tab}
              onChange={(id) => {
                setParam('tab', id === 'open' ? null : id);
                setSelected(new Set());
              }}
            />
          </div>
          <div style={{ minWidth: '200px' }}>
            <SearchableSelect
              value={clientId}
              onChange={(v) => setParam('client', v)}
              options={clients.map((c) => ({ value: c.id, label: c.name }))}
              placeholder="Every client"
              clearLabel="Every client"
              allowClear
            />
          </div>
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '6px' }}>
            {(Object.keys(TIERS) as Tier[])
              .filter((t) => tierCounts[t] > 0 || tierFilter === t)
              .map((t) => {
                const active = tierFilter === t;
                return (
                  <button
                    key={t}
                    className="pressable"
                    onClick={() => setParam('tier', active ? null : t)}
                    title={TIERS[t].blurb}
                    style={{
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--border-hairline)'}`,
                      background: active ? 'var(--accent-soft)' : 'var(--surface-card)',
                      color: active ? 'var(--accent-text)' : 'var(--text-muted)',
                      borderRadius: 'var(--radius-pill)',
                      padding: '3px 10px',
                      fontSize: 'var(--text-2xs)',
                      cursor: 'pointer',
                    }}
                  >
                    {t} {tierCounts[t]}
                  </button>
                );
              })}
            {[...kindCounts.entries()]
              .sort((a, b) => b[1] - a[1])
              .map(([kind, count]) => {
                const k = seoKind(kind);
                const active = kindFilter === kind;
                return (
                  <button
                    key={kind}
                    className="pressable"
                    onClick={() => setParam('kind', active ? null : kind)}
                    title={k.blurb}
                    style={{
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--border-hairline)'}`,
                      background: active ? 'var(--accent-soft)' : 'var(--surface-card)',
                      color: active ? 'var(--accent-text)' : 'var(--text-muted)',
                      borderRadius: 'var(--radius-pill)',
                      padding: '3px 10px',
                      fontSize: 'var(--text-2xs)',
                      cursor: 'pointer',
                    }}
                  >
                    {k.label} {count}
                  </button>
                );
              })}
          </span>
        </div>

        {loading ? (
          <div className="t-eyebrow" style={{ color: 'var(--text-faint)', padding: '40px 0', textAlign: 'center' }}>
            Loading...
          </div>
        ) : visible.length === 0 ? (
          <EmptyState tab={tab} onRefresh={onRefresh} busy={busy === 'detect'} />
        ) : (
          <>
            <div className="hidden md:block">
              <Table columns={columns} rows={visible} dense onRowClick={(row) => setOpenId(row.id)} />
            </div>
            {/* Phones: ten columns crushed into 358px overlapped each other, so each finding is a card */}
            <div
              className="md:hidden"
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-rest)',
              }}
            >
              {visible.map((row, i) => (
                <OpportunityCard
                  key={row.id}
                  row={row}
                  first={i === 0}
                  showClient={!clientId}
                  showStatus={tab === 'settled' || tab === 'queued' || tab === 'diagnosed'}
                  showAction={tab !== 'open'}
                  selected={selected.has(row.id)}
                  onToggle={() => toggle(row.id)}
                  onOpen={() => setOpenId(row.id)}
                  moving={busy === 'move'}
                  onMove={(to) => onMove([row.id], to)}
                />
              ))}
            </div>
          </>
        )}

        {/* What each chip means, so nobody has to ask what "cannibalized" is. */}
        {visible.length > 0 && kindFilter ? (
          <p style={{ marginTop: '12px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', maxWidth: '760px' }}>
            {SEO_KINDS[kindFilter]?.blurb}
          </p>
        ) : null}
      </main>

      <OpportunityPanel
        opportunity={openRow}
        onClose={() => setOpenId(null)}
        onChanged={load}
        onDiagnose={(id) => onDiagnose([id])}
      />
    </div>
  );
}

interface OpportunityCardProps {
  row: SeoOpportunity;
  first: boolean;
  showClient: boolean;
  showStatus: boolean;
  showAction: boolean;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  moving: boolean;
  onMove: (to: SeoStatus) => void;
}

/** The phone layout of one table row: same facts, stacked. */
function OpportunityCard({ row, first, showClient, showStatus, showAction, selected, onToggle, onOpen, moving, onMove }: OpportunityCardProps) {
  const k = seoKind(row.kind);
  const m = perMonth(row.est_click_gain);
  return (
    <div
      className="hover-wash"
      onClick={onOpen}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '12px 14px 12px 6px',
        borderTop: first ? 'none' : '1px solid var(--border-hairline)',
        cursor: 'pointer',
      }}
    >
      {/* A bigger hit area than the 16px box: the checkbox is how a phone selects work */}
      <span
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', flexShrink: 0, marginTop: '-6px' }}
      >
        <Checkbox checked={selected} onChange={onToggle} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
          <span style={{ color: 'var(--text-body)', fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-sm)', overflowWrap: 'anywhere' }}>
            {row.query}
          </span>
          <span
            className="t-num"
            style={{ flexShrink: 0, fontSize: 'var(--text-sm)', color: m && m >= 10 ? 'var(--status-up)' : 'var(--text-muted)' }}
          >
            {m == null ? '—' : `+${num(m)}/mo`}
          </span>
        </div>
        {row.url ? (
          <span className="t-num" title={row.url} style={{ ...clip, fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
            {row.url.replace(/^https?:\/\/(www\.)?/, '')}
          </span>
        ) : null}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
          {row.tier ? (
            <Badge tone={TIERS[row.tier].tone} title={row.tier_reason || TIERS[row.tier].blurb}>
              {row.tier}
            </Badge>
          ) : null}
          <Badge tone={k.tone} title={k.blurb}>
            {k.label}
          </Badge>
          {showClient && row.client?.name ? (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{row.client.name}</span>
          ) : null}
        </div>
        <div className="t-num" style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', marginTop: '6px', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          <span>Pos {num(row.baseline?.position, 1)}</span>
          <span>Impr {num(row.baseline?.impressions)}</span>
          <span>CTR {row.baseline?.ctr == null ? '—' : `${num(row.baseline.ctr, 1)}%`}</span>
        </div>
        {showAction || showStatus ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
            {showAction ? (
              row.action_type ? (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-body)' }}>
                  {SEO_ACTIONS[row.action_type as SeoActionType]?.label ?? row.action_type}
                </span>
              ) : row.status === 'diagnosing' ? (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-warn)' }}>with the agent</span>
              ) : null
            ) : null}
            {showStatus ? (
              <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
                <StatusMenu current={row.status} jobOut={row.status === 'diagnosing'} disabled={moving} onMove={onMove}>
                  <StatusTrigger status={row.status} disabled={moving} />
                </StatusMenu>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function EmptyState({ tab, onRefresh, busy }: { tab: Tab; onRefresh: () => void; busy: boolean }) {
  if (tab !== 'open') {
    return (
      <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
        Nothing here yet.
      </div>
    );
  }
  return (
    <div
      style={{
        padding: '48px 24px',
        textAlign: 'center',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      <p style={{ color: 'var(--text-body)', fontSize: 'var(--text-md)', marginBottom: '6px' }}>Nothing found yet</p>
      <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', maxWidth: '520px', margin: '0 auto 16px' }}>
        This reads the Search Console rows the nightly page sync already stored — no API calls, no Ahrefs units.
        If a client has never synced, run Pages on their detail screen first.
      </p>
      <Button size="sm" onClick={onRefresh} disabled={busy}>
        {busy ? 'Looking...' : 'Find opportunities'}
      </Button>
    </div>
  );
}
